import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { XCircle, CheckCircle2, Undo2, PlusCircle, Users, PhoneCall, PhoneOff, UserCheck, Store, LogOut, X } from 'lucide-react';
import { auth } from './firebase';

/**
 * ─────────────────────────────────────────────────────────────
 *  নতুন page যোগ করার নিয়ম (শুধু ২টা জায়গা):
 *   1) নিচের NAV_SECTIONS এ একটা item যোগ করুন  { key, label, path, icon }
 *   2) App.js এ সেই path এর <Route> যোগ করুন
 *  Active হাইলাইট URL দেখে নিজে নিজেই হয়, page থেকে কিছু পাঠাতে হয় না।
 *  Badge (সংখ্যা) দেখাতে চাইলে page থেকে counts={{ key: সংখ্যা }} পাঠান।
 * ─────────────────────────────────────────────────────────────
 */
export const NAV_SECTIONS = [
    {
        title: 'Orders',
        items: [
            { key: 'cancel', label: 'Cancel Orders', path: '/cancel-order', icon: XCircle },
            { key: 'delivered', label: 'Delivered Orders', path: '/delivered-order', icon: CheckCircle2 },
            { key: 'returned', label: 'Returned Orders', path: '/returned-order', icon: Undo2 },
            { key: 'create', label: 'Create Order', path: '/create-order', icon: PlusCircle },
        ],
    },
    {
        title: 'Calls',
        items: [
            { key: 'callreport', label: 'Call Report', path: '/call-report', icon: PhoneCall },
            { key: 'withoutorders', label: 'Without Orders', path: '/without-orders', icon: PhoneOff },
        ],
    },
    {
        title: 'Marketing',
        items: [
            { key: 'lead', label: 'Lead Manager', path: '/lead-manager', icon: Users },
        ],
    },
    {
        title: 'CRM',
        items: [
            { key: 'leadstore', label: 'Lead Store', path: '/stores', match: '/store/', icon: Store },
            { key: 'leadreseller', label: 'Lead Reseller', path: '/resellers', match: '/reseller/', icon: UserCheck },
        ],
    },
];

const SIDEBAR_W = 256; // w-64
const DESKTOP_QUERY = '(min-width: 1024px)';

/** ব্রাউজার ≥1024px হলে true (Tailwind এর lg) — resize করলেও আপডেট হয় */
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches,
    );
    useEffect(() => {
        const mq = window.matchMedia(DESKTOP_QUERY);
        const onChange = (e) => setIsDesktop(e.matches);
        setIsDesktop(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);
    return isDesktop;
}

function DashboardNav({ sidebarOpen, onClose, counts = {} }) {
    const isDesktop = useIsDesktop();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    return (
        <>
            {/* Mobile backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/30 z-30 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/*
                Collapse এর হিসাব inline style এ — Tailwind CDN এর runtime-জেনারেটেড ক্লাসের উপর নির্ভর করে না।
                Desktop: sticky + margin-left (-256px) দিয়ে জায়গাসহ গুটিয়ে যায়, পাশের কনটেন্ট বড় হয়।
                Mobile : fixed + translateX দিয়ে স্লাইড করে ঢোকে/বেরোয়।
            */}
            <aside
                aria-hidden={!sidebarOpen}
                className="top-0 left-0 z-40 h-screen w-64 flex-shrink-0 flex flex-col bg-white border-r border-gray-200"
                style={{
                    position: isDesktop ? 'sticky' : 'fixed',
                    marginLeft: isDesktop && !sidebarOpen ? -SIDEBAR_W : 0,
                    transform: !isDesktop && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
                    visibility: sidebarOpen ? 'visible' : 'hidden',
                    transition: `margin-left 300ms ease, transform 300ms ease, visibility 0s linear ${sidebarOpen ? '0s' : '300ms'}`,
                }}
            >
                {/* Brand */}
                <div className="flex items-center justify-between px-5 h-16 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-orange-400 text-white flex items-center justify-center font-bold">
                            E
                        </div>
                        <div className="leading-tight">
                            <div className="text-sm font-bold text-gray-900">ElevateMart</div>
                            <div className="text-xs text-gray-500">Business Analysis</div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                        title="Close menu"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Links */}
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
                    {NAV_SECTIONS.map((section) => (
                        <div key={section.title}>
                            <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                                {section.title}
                            </p>
                            <div className="space-y-1">
                                {section.items.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = pathname === item.path || (item.match && pathname.startsWith(item.match));
                                    const badge = counts[item.key];
                                    return (
                                        <button
                                            key={item.key}
                                            onClick={() => navigate(item.path)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition text-left
                                                ${isActive
                                                    ? 'bg-orange-100 text-orange-700'
                                                    : 'text-gray-600 hover:bg-orange-50 hover:text-orange-700'}`}
                                        >
                                            <Icon size={18} className={isActive ? 'text-orange-500' : 'text-gray-400'} />
                                            <span className="flex-1 truncate">{item.label}</span>
                                            {badge !== undefined && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-orange-400 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                                    {badge}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer */}
                <div className="px-3 py-4 border-t border-gray-200 flex-shrink-0">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-600 hover:bg-orange-50 hover:text-orange-700 transition"
                    >
                        <LogOut size={18} className="text-gray-400" />
                        Logout
                    </button>
                </div>
            </aside>
        </>
    );
}

export default DashboardNav;
